import type { Dispatch } from 'redux';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import spacetime from 'spacetime';

import { KnmPublishStatusesEnum } from 'APIPackage/KnmModule/enums/KnmPublishStatusesEnum';
import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';
import {
  getUserProfileIsTest,
  getUserRoleIsProsecutorOrSuperProsecutor,
  getUserRoleIsSupervisor,
} from 'AuthPackage/MainModule/selectors/authSelectors';
import { getIsSticked } from 'CorePackage/HeaderModule/reducers/headerReducer';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { KnmSystemTypesEnum } from 'KnmPackage/MainModule/enums/KnmSystemTypesEnum';
import {
  getKnmData,
  getKnmDataCreated,
  getKnmDataStartDate,
  getKnmEndByErknm,
  getKnmFormRef,
  getKnmIsBasicWatchOnly,
  getKnmIsEditEnabledByEditors,
  getKnmIsErknmStatusDecisionAppealed,
  getKnmIsErknmStatusTypeCantBeCarryOut,
  getKnmIsErknmStatusTypeCarriedOut,
  getKnmIsErknmStatusTypeCompleted,
  getKnmIsErknmStatusTypeWaitingCarryOut,
  getKnmIsErpStatusClosedErknm,
  getKnmIsErpStatusDeleted,
  getKnmIsErpStatusDisapproved,
  getKnmIsErpStatusNeedDoOverAgain,
  getKnmIsErpStatusSyspApprove,
  getKnmIsShowApproveRemarks,
  getKnmOnSaveButtonClick,
  getKnmPublishedStatus,
  getKnmPublishKnm,
  getKnmStatusText,
  getKnmSubjectTypeCode,
  getKnmSystemType,
  getKnmUnpublishKnm,
  getIsKnmCanBeSigned,
  getKnmIsErknmStatusReadyForApprove,
  getKnmErknmProfKindsSelectOptions,
  getKnmWatchOnlyByTestUser,
  getKnmIsTypeErknmPlanned,
  getKnmStatusHintFields,
  getKnmIsErknmStatusCommentsExist,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { Button } from 'UIPackage/MainModule/components/Button';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { KnmSubjectTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmSubjectTypeCodesEnum';
import { IEndCheckingErp } from 'APIPackage/SystemInoperabilityModule/interfaces/IEndCheckingErp';
import { knmErpMapStatusNameByNewName } from 'KnmErpPackage/MainModule/constants/knmErpMapStatusNameByNewName';
import { fetchEndCheckingErpList } from 'APIPackage/SystemInoperabilityModule/services/endCheckingErpApi';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { SYSTEM_DATE_FORMAT_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IProfKind } from 'APIPackage/DictionariesModule/interfaces/IProfKind';
import { ProfKindCodesEnum } from 'APIPackage/DictionariesModule/enums/ProfKindCodesEnum';
import { KindResultDecisionCodesEnum } from 'APIPackage/CatalogsModule/enums/KindResultDecisionCodesEnum';
import { fetchExcludedViolationsFromReinspection } from 'APIPackage/KnmModule/services/knmApi';
import { IKnmErknmFormViolatedRequirementsNpa } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormViolatedRequirementsNpa';
import { convertKnmDataRequirementsNpaToFormValues } from 'KnmErknmPackage/MainModule/common/convertKnmDataRequirementsNpaToFormValues';
import { KnmErknmStatusCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmErknmStatusCodesEnum';
import { getKnmPmStatusHasHintFields } from 'KnmPmPackage/MainModule/selectors/knmPmSelectors';
import {
  getKnmErknmIsPrescriptionFailed,
  getKnmErknmIsPrescriptionOldRules,
  getLatestKnmErknmChangePlanReasonApproveType,
} from 'KnmErknmPackage/MainModule/selectors/knmErknmSelectors';
import { KnmReasonTypeNameEnum } from 'KnmPackage/MainModule/enums/KnmReasonTypeNameEnum';

import {
  knmTryOpenSignModal,
  knmDelete,
  knmTestSign,
  knmTestSignWithSaveSigner,
  knmSetIsShowApproveRemarks,
} from '../../MainModule/actions/knmActions';
import { getIsPublishAvailable } from '../common/getIsPublishAvailable';
import styles from './KnmHeaderButtons.module.scss';
import { KnmHeaderDropdown } from './KnmHeaderDropdown';
import { getIsComplete336Available } from '../common/getIsComplete336Available';
import { KnmHeaderComplete336Modal } from './KnmHeaderComplete336Modal';
import { knmComplete336, knmHeaderHandleToApproveClick } from '../actions/knmHeaderActions';
import { ConfirmModal } from '../../../UIPackage/MainModule/components/ConfirmModal';
import { defineIsActive } from '../common/defineIsActive';
import { KnmTypeCodesEnum } from '../../../APIPackage/CatalogsModule/enums/KnmTypeCodesEnum';
import { getIsPlanErknmStatusApproved } from '../../../APIPackage/PlanListModule/common/getPlanErknmStatusFlags';
import { ErknmPlanStatusesEnum } from '../../../APIPackage/PlanListModule/enums/ErknmPlanStatusesEnum';
import { getPlanListPlanErknmStatus } from '../../../PlanListPackage/MainModule/selectors/planListSelectors';
import { isLaterThanAdminSetting } from '../../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from '../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { IAdminSetting } from '../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { getAdminFetchSettings } from '../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { ApprovedTypesEnum } from '../../../APIPackage/KnmModule/enums/ApprovedTypesEnum';
import { NotificationRepeatViolationModal } from './NotifcationRepeatViolationModal/NotificationRepeatViolationModal';

interface IKnmHeaderButtonsProps {
  className?: string;
  isSelfHidden?: true;
  location: 'header' | 'form';
  onMouseOverKnmSignButton?: () => void;
  onMouseOutKnmSignButton?: () => void;
}

interface IKnmHeaderButtonsStoreProps {
  knmSystemType: KnmSystemTypesEnum | undefined;
  isHeaderSticked: boolean;
  knmData: IKnm | undefined;
  publishedStatus: KnmPublishStatusesEnum | undefined;
  knmEndByErknm: (() => void) | undefined;
  publishKnm: (() => void) | undefined;
  unpublishKnm: (() => void) | undefined;
  onSaveButtonClick: (() => void) | undefined;
  isProsecutorOrSuperProsecutor: boolean | undefined;
  isSupervisor: boolean | undefined;
  isErpStatusDeleted: boolean;
  isErpStatusClosedErknm: boolean;
  isErpStatusDisapproved: boolean;
  isErpStatusSyspApprove: boolean;
  isErpStatusNeedDoOverAgain: boolean;
  isKnmEditEnabledByEditors: boolean;
  isErknmStatusTypeWaitingCarryOut: boolean;
  isErknmStatusTypeCarriedOut: boolean;
  isErknmStatusReadyForApprove: boolean;
  isErknmStatusTypeCompleted: boolean;
  isErknmStatusDecisionAppealed: boolean;
  isErknmStatusTypeCantBeCarryOut: boolean;
  erknmCreatedDate: string | null | undefined;
  erknmStartDate: string | null | undefined;
  knmSubjectTypeCode: KnmSubjectTypeCodesEnum | '' | undefined;
  isPm: boolean;
  isBasicWatchOnly: boolean;
  formRef: IKnmFormRef | undefined;
  statusText: string | undefined;
  isKnmCanBeSigned: boolean | undefined;
  knmPlanStatus: ErknmPlanStatusesEnum | undefined;
  settings: IAdminSetting[];
  isUserTest: boolean;
  isWatchOnlyByTestUser: boolean;
  pmTypesSelectOptions: IDictionarySelectOption<IProfKind>[] | undefined;
  isShowApproveRemarks: boolean | undefined;
  pmStatusHasHintFields?: boolean;
  changePlanReasonApproveType: ApprovedTypesEnum | undefined;
  isErknmTypePlanned: boolean;
  isPrescriptionCheckByOldRules?: boolean | undefined;
  isPassportByCheckPrescriptionFailed?: boolean | undefined;
  isErknmStatusCommentsExist: boolean;
  erknmStatusHasHintFields: boolean;
}

const KnmHeaderButtonsWithoutStore = ({
  className,
  isSelfHidden,
  knmSystemType,
  location,
  isHeaderSticked,
  knmData,
  publishedStatus,
  onSaveButtonClick,
  knmEndByErknm,
  publishKnm,
  unpublishKnm,
  erknmCreatedDate,
  erknmStartDate,
  isErknmStatusTypeCarriedOut,
  isErknmStatusTypeWaitingCarryOut,
  isErknmStatusReadyForApprove,
  isErknmStatusTypeCompleted,
  isErknmStatusDecisionAppealed,
  isErknmStatusTypeCantBeCarryOut,
  isErpStatusDeleted,
  isErpStatusClosedErknm,
  isErpStatusDisapproved,
  isErpStatusNeedDoOverAgain,
  isErpStatusSyspApprove,
  isKnmEditEnabledByEditors,
  isProsecutorOrSuperProsecutor,
  isSupervisor,
  knmSubjectTypeCode,
  isPm,
  isBasicWatchOnly,
  statusText,
  formRef,
  isKnmCanBeSigned,
  knmPlanStatus,
  settings,
  onMouseOverKnmSignButton,
  onMouseOutKnmSignButton,
  isUserTest,
  isWatchOnlyByTestUser,
  pmTypesSelectOptions,
  isShowApproveRemarks,
  pmStatusHasHintFields,
  changePlanReasonApproveType,
  isErknmTypePlanned,
  isPrescriptionCheckByOldRules,
  isPassportByCheckPrescriptionFailed,
  isErknmStatusCommentsExist,
  erknmStatusHasHintFields,
}: IKnmHeaderButtonsProps & IKnmHeaderButtonsStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [endErpSettings, setEndErpSettings] = useState<IEndCheckingErp[] | null>(null);
  const [isActive, setIsActive] = useState(defineIsActive(location, isHeaderSticked));

  const isErp = knmSystemType === KnmSystemTypesEnum.ERP;
  const isErknm = knmSystemType === KnmSystemTypesEnum.ERKNM;
  const isKnmPlanErknmStatusApproved: boolean = getIsPlanErknmStatusApproved(knmPlanStatus);
  const [isRequirementsNpaModalVisible, setIsRequirementsNpaModalVisible] = useState(false);
  const [willBeRemoveRequirements, setWillBeRemoveRequirements] = useState<IKnmErknmFormViolatedRequirementsNpa[]>([]);
  const requirementsNew = formRef?.current.getFieldState('requirementsNpaErknm')?.value;
  const isLaterThanFieldsMarksSetting = isLaterThanAdminSetting(
    knmData?.created,
    settings,
    AdminSettingCodesEnum.FIELDS_MARKS_481_4_1_14,
  );

  const isLaterThanKnmValidationSetting = isLaterThanAdminSetting(
    knmData?.created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_523_4_1_24_STARTS_FROM,
  );

  const isEnablePrescriptionCheck = isLaterThanAdminSetting(
    knmData?.created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_105_166_25_4_1_30_STARTS_FROM,
  );

  const isPublishAvailable: boolean = getIsPublishAvailable(
    isErpStatusDeleted,
    isErpStatusDisapproved,
    isErpStatusSyspApprove,
    isErpStatusNeedDoOverAgain,
  );

  const isComplete336Available: boolean = getIsComplete336Available(
    isErp,
    isErknmStatusTypeWaitingCarryOut,
    isErknmStatusTypeCarriedOut,
    erknmCreatedDate,
    erknmStartDate,
    knmSubjectTypeCode,
  );

  const isShowToApproveButton =
    isErknm &&
    isSupervisor &&
    isErknmStatusReadyForApprove &&
    (knmData?.type?.code === KnmTypeCodesEnum.PP ? isKnmPlanErknmStatusApproved : true);

  const [isFormValid, setIsFormValid] = useState(() => !(formRef?.current?.getState().invalid ?? true));

  useEffect(() => {
    if (!formRef?.current) {
      return undefined;
    }

    setIsFormValid(!formRef.current.getState().invalid);

    const unsubscribe = formRef.current.subscribe(
      ({ invalid }) => {
        setIsFormValid(!invalid);
      },
      { invalid: true },
    );

    return () => {
      unsubscribe?.();
    };
  }, [formRef]);

  const isErknmSignBlockedByHintFieldsOnCommentsExist = isErknmStatusCommentsExist && erknmStatusHasHintFields;

  const isSignButtonEnabled =
    isKnmCanBeSigned &&
    isKnmEditEnabledByEditors &&
    (!isPm || !pmStatusHasHintFields) &&
    !isErknmSignBlockedByHintFieldsOnCommentsExist &&
    isFormValid;

  const isDeniedChangePlanReasonForProsecutor =
    isProsecutorOrSuperProsecutor &&
    isErknmTypePlanned &&
    isErknmStatusTypeWaitingCarryOut &&
    changePlanReasonApproveType === ApprovedTypesEnum.DENIED;

  const [isComplete336ModalVisible, setIsComplete336ModalVisible] = useState(false);

  const handleComplete336ModalApply = useCallback(
    (id?: number) => {
      setIsComplete336ModalVisible(false);
      if (id) {
        dispatch(knmComplete336(id));
      }
    },
    [dispatch],
  );

  const handleComplete336ModalClose = useCallback(() => setIsComplete336ModalVisible(false), []);
  const onComplete336ButtonClick = useCallback(() => setIsComplete336ModalVisible(true), []);

  useEffect(() => {
    setIsActive(defineIsActive(location, isHeaderSticked));
  }, [location, isHeaderSticked]);

  /**
   * Запрос на получение списка всех настроек
   */
  const fetchAllSettings = useCallback(async () => {
    const settingsList = await fetchEndCheckingErpList();

    setEndErpSettings(settingsList);
  }, []);

  /**
   * Загрузка справочника нстроек
   */
  useEffect(() => {
    if (
      location === 'form' &&
      isErp &&
      !endErpSettings &&
      statusText &&
      knmErpMapStatusNameByNewName[statusText] === 'В процессе проведения'
    ) {
      fetchAllSettings();
    }
  }, [location, isErp, statusText, setEndErpSettings]);

  const headerAttributes = {
    ...(!isActive && isSelfHidden && { 'data-hidden': true }),
  };

  const [isConfirmDeleteModalVisible, setIsConfirmDeleteModalVisible] = useState(false);
  const handleDeleteKnm = () => setIsConfirmDeleteModalVisible(true);

  const handleDeleteClick = useCallback(() => {
    setIsConfirmDeleteModalVisible(true);
    dispatch(knmDelete());
  }, [dispatch]);

  const handleToApproveButtonClick = () => dispatch(knmHeaderHandleToApproveClick());

  const handleDeleteModalClose = useCallback(() => setIsConfirmDeleteModalVisible(false), []);

  const confirmModalDeleteText = isPm ? `Удалить паспорт ПМ?` : `Удалить паспорт КНМ?`;

  const supervisionType = formRef?.current.getFieldState('supervisionType')?.value;
  const startDate = formRef?.current.getFieldState('startDate')?.value;
  const stopDate = formRef?.current.getFieldState('stopDate')?.value;
  const type = formRef?.current.getFieldState('type')?.value;

  /**
   * Формируем значение флага для отображения кнопки "Завершить в рамках ЕРКНМ"
   */
  const isVisibleButtonEndByErvk = useMemo(() => {
    if (
      type?.code !== 'KZ' ||
      !supervisionType ||
      !endErpSettings ||
      !startDate ||
      !stopDate ||
      (statusText && knmErpMapStatusNameByNewName[statusText] !== 'В процессе проведения')
    ) {
      return false;
    }

    const result = endErpSettings.find(i => {
      if (i.supervisionTypeIds?.find(j => j === supervisionType.id) || i.all) {
        const fromDate = spacetime(i.fromDate || '');
        const toDate = spacetime(i.toDate || '');
        const startDateObj = spacetime(formatDate({ date: startDate, dateFormat: SYSTEM_DATE_FORMAT_FNS }));
        const stopDateObj = spacetime(formatDate({ date: stopDate, dateFormat: SYSTEM_DATE_FORMAT_FNS }));

        const isResultDateStartValid =
          (startDateObj.isAfter(fromDate) || startDateObj.isEqual(fromDate)) &&
          (startDateObj.isBefore(toDate) || startDateObj.isEqual(toDate));

        const isResultDateStopValid =
          (stopDateObj.isAfter(fromDate) || startDateObj.isEqual(fromDate)) &&
          (stopDateObj.isBefore(toDate) || stopDateObj.isEqual(toDate));

        return isResultDateStartValid || isResultDateStopValid;
      }

      return false;
    });

    return !!result;
  }, [supervisionType, endErpSettings, startDate, stopDate, statusText]);

  const handleSignButtonHover = useCallback(
    (isHover: boolean) => {
      if (isHover && !isSignButtonEnabled) {
        onMouseOverKnmSignButton && onMouseOverKnmSignButton();
      } else {
        onMouseOutKnmSignButton && onMouseOutKnmSignButton();
      }
    },
    [isSignButtonEnabled, onMouseOverKnmSignButton, onMouseOutKnmSignButton],
  );

  /**
   * Валидация неактивных нарушений при повторной проверке
   * если требование выведено в прод и у паспорта основание 'Проверка предписания'
   */
  const validateNotActiveViolations = useCallback(() => {
    const isPrescriptionCheck =
      knmData?.knmErknm?.reasons?.[0].reason?.type?.name === KnmReasonTypeNameEnum.PRESCRIPTION_CHECK &&
      isPrescriptionCheckByOldRules === false;

    if (isEnablePrescriptionCheck && (isPrescriptionCheck || isPassportByCheckPrescriptionFailed === true) && knmData) {
      return fetchExcludedViolationsFromReinspection(knmData.id).then(requirements => {
        if (requirements?.length) {
          // Показываем модальное окно с предупреждением
          setIsRequirementsNpaModalVisible(true);
          setWillBeRemoveRequirements(convertKnmDataRequirementsNpaToFormValues(requirements) || []);

          return false;
        }

        return true;
      });
    }

    return new Promise<boolean>(resolve => resolve(true));
  }, [knmData, isEnablePrescriptionCheck, formRef, isPrescriptionCheckByOldRules, isPassportByCheckPrescriptionFailed]);

  /**
   * Обработка нажатия кнопки "Подписать паспорт"
   */
  const testSignOrOpenSignModal = useCallback(() => {
    if (!isUserTest) {
      dispatch(knmTryOpenSignModal());
    } else if (knmData?.id) {
      if (knmSystemType === KnmSystemTypesEnum.PM) {
        const pmType = pmTypesSelectOptions?.find(
          i =>
            i.value.dictId === knmData.pmErknm?.pmType?.dictId &&
            i.value.dictVersionId === knmData.pmErknm?.pmType?.dictVersionId,
        );

        if (pmType?.code === ProfKindCodesEnum.PP) {
          dispatch(knmTestSign(knmData.id));
        } else if (pmType?.code === ProfKindCodesEnum.OP) {
          const signer = knmData?.pmErknm?.inspectors?.[0];

          if (signer) {
            dispatch(knmTestSignWithSaveSigner(knmData.id, signer));
          }
        }
      } else {
        dispatch(knmTestSign(knmData.id));
      }
    }
  }, [isUserTest, pmTypesSelectOptions, knmSystemType, knmData]);

  /**
   * Обработка нажатия кнопки "Подписать паспорт"
   */
  const onClickButtonSign = useCallback(async () => {
    if (
      knmData?.knmErknm &&
      (knmData?.status?.code === KnmErknmStatusCodesEnum.FILL_IN_PROCESS ||
        knmData?.status?.code === KnmErknmStatusCodesEnum.READY_FOR_APPROVE)
    ) {
      const isValid = await validateNotActiveViolations();

      if (!isValid) return null;
    }

    testSignOrOpenSignModal();
  }, [testSignOrOpenSignModal, validateNotActiveViolations, knmData]);

  const invertIsShowApproveRemarks = useCallback(() => {
    dispatch(knmSetIsShowApproveRemarks(!isShowApproveRemarks));
  }, [isShowApproveRemarks, dispatch]);

  const typeErknm = knmData?.type;
  const approved = knmData?.knmErknm?.approve?.approved;
  const isShowApproveRemarksButton =
    typeErknm?.code === KnmTypeCodesEnum.PP &&
    approved === ApprovedTypesEnum.APPROVED_WITH_REMARK &&
    (isErknmStatusTypeWaitingCarryOut ||
      isErknmStatusTypeCarriedOut ||
      isErknmStatusTypeCompleted ||
      isErknmStatusDecisionAppealed ||
      isErknmStatusTypeCantBeCarryOut);

  const hasUnsignedAct = useMemo(() => {
    return (
      !!knmData?.knmErknm?.organizations?.find(i => (i.act ? !i.act.signed && i.act.paperless : false)) ||
      (!!knmData?.pmErknm?.isUnableToRefuse &&
        (knmData?.pmErknm?.subject?.act
          ? !knmData?.pmErknm?.subject?.act?.signed && knmData?.pmErknm?.subject?.act.paperless
          : false))
    );
  }, [knmData]);

  const resultDecisions = formRef?.current.getFieldState('resultDecisions')?.value;
  const pmResultDecisions = formRef?.current.getFieldState('pmResultDecisions')?.value;

  const hasUnsignedResultDecision = useMemo(() => {
    return (
      !!resultDecisions?.find(
        rd =>
          (rd.kindDecision?.code === KindResultDecisionCodesEnum.D_1 ||
            rd.kindDecision?.code === KindResultDecisionCodesEnum.D_7) &&
          rd.paperless &&
          !rd.signed,
      ) ||
      !!pmResultDecisions?.find(
        rd =>
          (rd.kindDecision?.code === KindResultDecisionCodesEnum.D_1 ||
            rd.kindDecision?.code === KindResultDecisionCodesEnum.D_7) &&
          rd.paperless &&
          !rd.signed,
      )
    );
  }, [formRef?.current, JSON.stringify(resultDecisions), JSON.stringify(pmResultDecisions)]);

  return (
    <div className={`${styles.Container} ${className}`} {...headerAttributes}>
      {(isProsecutorOrSuperProsecutor || isSupervisor) && (
        <>
          {isShowToApproveButton &&
            isLaterThanFieldsMarksSetting &&
            !isWatchOnlyByTestUser &&
            knmData?.signed &&
            !knmData?.resigningRequired && (
              <Button
                text="Отправить на согласование"
                color="primary"
                variant="minimal"
                className={styles.OnApproveButton}
                type="button"
                disabled={!isKnmEditEnabledByEditors}
                onClick={handleToApproveButtonClick}
              />
            )}
          {isSupervisor && !isPm && isComplete336Available && (
            <Button
              className={styles.Complete336Button}
              color="danger"
              text="Завершить ПП 336"
              id="complete336Button"
              tooltip="Осуществляется завершение КНМ на основании постановления № 336 от 10.03.2022 г."
              type="button"
              onClick={onComplete336ButtonClick}
            />
          )}
          {!hasUnsignedResultDecision &&
            !hasUnsignedAct &&
            !isErp &&
            (!knmData?.signed || knmData.resigningRequired) &&
            !isWatchOnlyByTestUser &&
            !knmData?.deleted &&
            !isDeniedChangePlanReasonForProsecutor &&
            !isProsecutorOrSuperProsecutor && (
              <Button
                className={styles.SignButton}
                text="Подписать паспорт"
                color="primary"
                variant="minimal"
                id="signButton"
                type="button"
                onClick={onClickButtonSign}
                disabled={!isSignButtonEnabled}
                onMouseOver={() => handleSignButtonHover(true)}
                onMouseOut={() => handleSignButtonHover(false)}
              />
            )}
          {isVisibleButtonEndByErvk && (
            <Button
              className={styles.Complete336Button}
              color="primary"
              text="Завершить в рамках ЕРКНМ"
              id="endByErknmButton"
              type={'button'}
              onClick={knmEndByErknm}
            />
          )}
          {isProsecutorOrSuperProsecutor && isErknm && isShowApproveRemarksButton && (
            <Button
              className={styles.ShowApproveRemarksButton}
              text={isShowApproveRemarks ? 'Скрыть замечания' : 'Показать замечания'}
              type="button"
              id="showApproveRemarksButton"
              onClick={invertIsShowApproveRemarks}
            />
          )}
          {!isErpStatusClosedErknm && !isWatchOnlyByTestUser && (
            <Button
              color="primary"
              text="Сохранить"
              id="saveButton"
              type={onSaveButtonClick ? 'button' : 'submit'}
              disabled={knmData?.deleted || !isKnmEditEnabledByEditors || isBasicWatchOnly}
              onClick={onSaveButtonClick}
            />
          )}
          {!isLaterThanKnmValidationSetting && (
            <>
              {isErp &&
                ((isPublishAvailable &&
                  (!publishedStatus || publishedStatus === KnmPublishStatusesEnum.UNPUBLISHED) && (
                    <Button
                      className={styles.PublishButton}
                      color="danger"
                      text="Не опубликовано"
                      type="button"
                      disabled={knmData?.deleted || Boolean(!knmData?.id) || isErpStatusClosedErknm}
                      onClick={publishKnm}
                    />
                  )) ||
                  (publishedStatus === KnmPublishStatusesEnum.ASK_TO_PUBLISH && (
                    <Button
                      className={styles.PublishButton}
                      color="warning"
                      text="Ожидает публикации"
                      type="button"
                      disabled={knmData?.deleted || isErpStatusClosedErknm}
                      onClick={unpublishKnm}
                    />
                  )) ||
                  (publishedStatus === KnmPublishStatusesEnum.PUBLISHED && (
                    <Button
                      className={styles.PublishButton}
                      color="success"
                      text="Опубликовано"
                      type="button"
                      disabled={knmData?.deleted || isErpStatusClosedErknm}
                      onClick={unpublishKnm}
                    />
                  )))}

              {!isErpStatusClosedErknm &&
                isPublishAvailable &&
                publishedStatus === KnmPublishStatusesEnum.ASK_TO_UNPUBLISH && (
                  <Button
                    className={styles.PublishButton}
                    color="warning"
                    text="Ожидает отзыва"
                    type="button"
                    disabled={knmData?.deleted}
                    onClick={publishKnm}
                  />
                )}
            </>
          )}
        </>
      )}
      {!isWatchOnlyByTestUser && (
        <KnmHeaderDropdown
          location={location}
          handleDeleteKnm={handleDeleteKnm}
          isShowErknmToApproveButton={isShowToApproveButton}
          onToApproveButtonClick={handleToApproveButtonClick}
          enabledByTestUser={!isWatchOnlyByTestUser}
          onClickButtonSign={onClickButtonSign}
        />
      )}
      <ConfirmModal
        visible={isConfirmDeleteModalVisible}
        title="Подтверждение"
        text={confirmModalDeleteText}
        applyButtonText="Удалить"
        cancelButtonText="Отменить"
        onApply={handleDeleteClick}
        onClose={handleDeleteModalClose}
      />
      <KnmHeaderComplete336Modal
        visible={isComplete336ModalVisible}
        id={knmData?.id}
        isErp={isErp}
        onApply={handleComplete336ModalApply}
        onClose={handleComplete336ModalClose}
      />
      <NotificationRepeatViolationModal
        requirementsCurrent={requirementsNew}
        requirementsNpa={willBeRemoveRequirements}
        visible={isRequirementsNpaModalVisible}
        onClose={() => setIsRequirementsNpaModalVisible(false)}
        onSubmit={testSignOrOpenSignModal}
      />
    </div>
  );
};

const mapStateToProps = (state: IRootState): IKnmHeaderButtonsStoreProps => ({
  knmSystemType: getKnmSystemType(state),
  isHeaderSticked: getIsSticked(state),
  knmData: getKnmData(state),
  publishedStatus: getKnmPublishedStatus(state),
  publishKnm: getKnmPublishKnm(state),
  knmEndByErknm: getKnmEndByErknm(state),
  unpublishKnm: getKnmUnpublishKnm(state),
  onSaveButtonClick: getKnmOnSaveButtonClick(state),
  isProsecutorOrSuperProsecutor: getUserRoleIsProsecutorOrSuperProsecutor(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  isErpStatusDeleted: getKnmIsErpStatusDeleted(state),
  isErpStatusClosedErknm: getKnmIsErpStatusClosedErknm(state),
  isErpStatusDisapproved: getKnmIsErpStatusDisapproved(state),
  isErpStatusSyspApprove: getKnmIsErpStatusSyspApprove(state),
  isErpStatusNeedDoOverAgain: getKnmIsErpStatusNeedDoOverAgain(state),
  isKnmEditEnabledByEditors: getKnmIsEditEnabledByEditors(state),
  isErknmStatusTypeWaitingCarryOut: getKnmIsErknmStatusTypeWaitingCarryOut(state),
  isErknmStatusTypeCarriedOut: getKnmIsErknmStatusTypeCarriedOut(state),
  isErknmStatusReadyForApprove: getKnmIsErknmStatusReadyForApprove(state),
  isErknmStatusTypeCompleted: getKnmIsErknmStatusTypeCompleted(state),
  isErknmStatusDecisionAppealed: getKnmIsErknmStatusDecisionAppealed(state),
  isErknmStatusTypeCantBeCarryOut: getKnmIsErknmStatusTypeCantBeCarryOut(state),
  erknmCreatedDate: getKnmDataCreated(state),
  erknmStartDate: getKnmDataStartDate(state),
  knmSubjectTypeCode: getKnmSubjectTypeCode(state),
  isBasicWatchOnly: getKnmIsBasicWatchOnly(state),
  formRef: getKnmFormRef(state),
  isPm: getKnmSystemType(state) === KnmSystemTypesEnum.PM,
  statusText: getKnmStatusText(state),
  isKnmCanBeSigned: getIsKnmCanBeSigned(state),
  knmPlanStatus: getPlanListPlanErknmStatus(state),
  settings: getAdminFetchSettings(state),
  isUserTest: getUserProfileIsTest(state),
  pmTypesSelectOptions: getKnmErknmProfKindsSelectOptions(state),
  isWatchOnlyByTestUser: getKnmWatchOnlyByTestUser(state),
  isShowApproveRemarks: getKnmIsShowApproveRemarks(state),
  pmStatusHasHintFields: getKnmPmStatusHasHintFields(state),
  changePlanReasonApproveType: getLatestKnmErknmChangePlanReasonApproveType(state),
  isErknmTypePlanned: getKnmIsTypeErknmPlanned(state),
  isPrescriptionCheckByOldRules: getKnmErknmIsPrescriptionOldRules(state),
  isPassportByCheckPrescriptionFailed: getKnmErknmIsPrescriptionFailed(state),
  isErknmStatusCommentsExist: getKnmIsErknmStatusCommentsExist(state),
  erknmStatusHasHintFields: !!getKnmStatusHintFields(state)?.length,
});

export const KnmHeaderButtons = connect(mapStateToProps)(KnmHeaderButtonsWithoutStore);
